import type { UserFilters } from '~/pages/backoffice/admin/user/list/use-user-filters'
import { useLazyQuery } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_USERS_WITH_FILTERS } from '~/gql/users/users'
import { usersCsvHeaders, usersCsvRow } from '~/pages/backoffice/admin/user/list/users-csv'
import { EXPORT_MAX_ROWS, fetchAllForExport } from '~/pages/backoffice/exportPagination'
import { downloadCSV } from '~/utils/exportCsv/utils'

/**
 * Export CSV de la liste des comptes, par tranches, avec la recherche et les
 * filtres de l'écran : sortir « les 40 apprenants du client A » est précisément
 * ce que l'export sert à faire (#135).
 *
 * `fileName` : le fichier porte le nom de l'écran d'où il sort.
 */
export function useUsersExport({ query, filterBy }: Pick<UserFilters, 'query' | 'filterBy'>, fileName: string) {
  const { t, i18n } = useTranslation()
  // network-only : l'export part en plusieurs tranches, qui doivent toutes venir
  // de la même lecture serveur — une tranche servie depuis le cache mélangerait
  // deux états de la base dans un même fichier.
  const [fetchExportedUsers] = useLazyQuery(GQL_USERS_WITH_FILTERS, { fetchPolicy: 'network-only' })

  return async function exportCsv() {
    const exported = await fetchAllForExport(async (skip, take) => {
      const chunk = await fetchExportedUsers({
        variables: { input: { skip, take, searchInput: query, filterBy } },
      })
      const result = chunk.data?.getFilteredUsers
      return result?.users ? { rows: result.users, total: result.total } : null
    })
    if (!exported) return

    if (exported.truncated) {
      toast.warning(t('export.truncated', { max: EXPORT_MAX_ROWS, total: exported.total }))
    }

    downloadCSV(
      fileName,
      usersCsvHeaders(t),
      exported.rows.map(user => usersCsvRow(t, i18n.language, user)),
    )
  }
}
